import React, {
	FC,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { SATOSLink } from "@/app/application/SATOSLink";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { stringAvatar } from "@/features/procedures/lib/checklist";
import type {
	ProcedureUpdateComment,
	ProcedureUpdateSuggestionAttachment,
	ProcedureUpdateSuggestionResponse,
} from "@/features/procedures/model/types/procedureUpdateSuggestions";
import { AlertConfirmDeleteComment } from "@/shared/notifications/AlertConfirm";
import { ErrorToast, SuccessToast } from "@/shared/notifications/AlertToast";
import {
	AttachFileOutlined as AttachFileOutlinedIcon,
	AutoAwesome as AutoAwesomeIcon,
	ChatBubbleOutline as ChatBubbleOutlineIcon,
	CheckCircle as CheckCircleIcon,
	Clear as ClearIcon,
	Close as CloseIcon,
	Comment as CommentIcon,
	Done as DoneIcon,
	Error as ErrorIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	HighlightOffOutlined as HighlightOffOutlinedIcon,
	HourglassEmpty as HourglassEmptyIcon,
	MoreVert as MoreVertIcon,
	OpenInNew as OpenInNewIcon,
	Save as SaveIcon,
} from "@mui/icons-material";
import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Fade,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import type { MenuProps } from "@mui/material/Menu";
import type { Theme } from "@mui/material/styles";
import { alpha, styled } from "@mui/material/styles";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import rehypeRaw from "rehype-raw";

import { checkAdminAccess } from "@/service/ApplicationQueries";
import { useAddProcedureUpdateSuggestionComment } from "@/service/ProcedureMutations";
import { Language } from "@/model/Language/Language";


dayjs.extend(utc);
dayjs.extend(tz);

type Suggestion = ProcedureUpdateSuggestionResponse & {
	ProcedureName?: string | null;
};

interface Props {
	suggestion: Suggestion;
	selectedCard: boolean;
	page: string;
	selectedChecklistName?: string | null;
	selectedChecklistProcedureId?: number | null;
	selectedChecklistId?: number | null;
	onOpenSuggestion?: (suggestion: Suggestion) => void;
	handleStepIdSelected: (stepId: number) => void;
	handleUpdateStatusForCard: (
		suggestionId: number,
		status: string,
		extra: {
			procedureId: number;
			stepId: number;
			description: string;
			anonymous: boolean;
			attachment?: ProcedureUpdateSuggestionAttachment | null;
			declinedComment?: string;
			checklistId?: number | null;
		},
	) => Promise<void> | void;
	handleDeleteComment: (commentId: number) => Promise<void> | void;
	refreshSuggestions: () => Promise<void> | void;
}

/* ------------------------- helpers ------------------------- */

const isTypingTarget = (el: EventTarget | null) => {
	if (!el || !(el as HTMLElement).tagName) return false;
	const node = el as HTMLElement;
	const tag = node.tagName.toLowerCase();
	const editable = node.getAttribute?.("contenteditable") === "true";
	return editable || tag === "input" || tag === "textarea" || tag === "select";
};

type Tone = "default" | "success" | "warning" | "error" | "info";

const statusVisual = (status?: string) => {
	const s = String(status || "").toLowerCase();
	if (s === "verified")
		return {
			tone: "success" as Tone,
			label: "Verified",
			icon: <CheckCircleIcon fontSize="small" />,
		};
	if (s === "declined")
		return {
			tone: "error" as Tone,
			label: "Declined",
			icon: <ErrorIcon fontSize="small" />,
		};
	if (s === "closed")
		return {
			tone: "default" as Tone,
			label: "Closed",
			icon: <HighlightOffOutlinedIcon fontSize="small" />,
		};
	return {
		tone: "warning" as Tone,
		label: "Unverified",
		icon: <HourglassEmptyIcon fontSize="small" />,
	};
};

const toneToMuiColor = (tone: Tone) =>
	tone === "success"
		? ("success" as const)
		: tone === "warning"
			? ("warning" as const)
			: tone === "error"
				? ("error" as const)
				: tone === "info"
					? ("info" as const)
					: ("default" as const);

const toneColor = (t: Theme, tone: Tone) =>
	tone === "success"
		? t.palette.success.main
		: tone === "warning"
			? t.palette.warning.main
			: tone === "error"
				? t.palette.error.main
				: tone === "info"
					? t.palette.info.main
					: t.palette.text.secondary;

type ActionItem = {
	label: string;
	icon: React.ReactNode;
	action: () => Promise<void> | void;
	tone?: Tone;
	desc?: string;
	kbd?: string;
	dividerAbove?: boolean;
	disabled?: boolean;
};

type SuggestionStatusKey = "unverified" | "verified" | "declined" | "closed";
type SuggestedChoiceKey = "verify" | "decline" | "close" | "unverified";

const normalizeSuggestionStatus = (status?: string): SuggestionStatusKey => {
	const s = String(status || "").toLowerCase();
	if (s === "verified") return "verified";
	if (s === "declined") return "declined";
	if (s === "closed") return "closed";
	return "unverified";
};

const getSuggestedChoiceStatus = (
	page: string,
	status?: string,
): SuggestionStatusKey => {
	switch (page) {
		case "newImprov":
			return "unverified";
		case "verified":
			return "verified";
		case "declined":
			return "declined";
		case "closed":
			return "closed";
		case "myImprov":
		default:
			return normalizeSuggestionStatus(status);
	}
};

const suggestedChoicesByStatus: Record<
	SuggestionStatusKey,
	SuggestedChoiceKey[]
> = {
	unverified: ["verify", "decline", "close"],
	verified: ["unverified", "decline", "close"],
	declined: ["unverified", "verify", "close"],
	closed: ["unverified"],
};

/* ------------------------- menu ------------------------- */

const CompactMenu = styled((props: MenuProps) => (
	<Menu
		elevation={0}
		keepMounted
		TransitionComponent={Fade}
		anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
		transformOrigin={{ vertical: "top", horizontal: "right" }}
		{...props}
	/>
))(({ theme }) => ({
	"& .MuiPaper-root": {
		borderRadius: 14,
		marginTop: theme.spacing(1),
		minWidth: 280,
		overflow: "hidden",
		border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[6],
	},
	"& .MuiMenu-list": {
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
	},
	"& .MuiMenuItem-root": {
		margin: theme.spacing(0.25, 0.75),
		borderRadius: 10,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		paddingTop: theme.spacing(0.75),
		paddingBottom: theme.spacing(0.75),
		minHeight: 40,
		alignItems: "center",
		"&:hover": { backgroundColor: theme.palette.action.hover },
		"&:active": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
		"& .MuiListItemIcon-root": {
			minWidth: 32,
			color: theme.palette.text.primary,
		},
		"& .MuiListItemText-primary": {
			fontSize: 14.5,
			fontWeight: 650,
			letterSpacing: 0.1,
		},
		"& .MuiListItemText-secondary": {
			fontSize: 12,
			lineHeight: 1.2,
			marginTop: theme.spacing(0.25),
			color: theme.palette.text.secondary,
		},
	},
}));

const compactDividerSx = { my: 0.6, mx: 1.5 };

/* ------------------------- component ------------------------- */

const CustomCard: FC<Props> = ({
	handleDeleteComment,
	selectedChecklistProcedureId,
	selectedChecklistId,
	suggestion,
	selectedCard,
	handleUpdateStatusForCard,
	handleStepIdSelected,
	page,
	refreshSuggestions,
	onOpenSuggestion,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const { timezone } = useTime();
	const user = useContext(UserContext);

	const [
		userCanApproveAndDeclineSuggestions,
		setUserCanApproveAndDeclineSuggestions,
	] = useState(false);

	const [isDeclineModalOpen, setDeclineModalOpen] = useState(false);
	const [isCommentModalOpen, setCommentModalOpen] = useState(false);
	const [declineComment, setDeclineComment] = useState("");
	const [comment, setComment] = useState("");

	const [isSubmittingComment, setIsSubmittingComment] = useState(false);
	const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
	const [pendingStatus, setPendingStatus] = useState<string | null>(null);
	const [deletingReplyId, setDeletingReplyId] = useState<number | null>(null);

	const [expanded, setExpanded] = useState(false);

	// Per-comment menu
	const [commentMenuAnchor, setCommentMenuAnchor] =
		useState<null | HTMLElement>(null);
	const [activeCommentId, setActiveCommentId] = useState<number | null>(null);

	const AddComment = useAddProcedureUpdateSuggestionComment();

	useEffect(() => {
		const canApprove =
			user?.IsProcedureSuggestionsAdministrator ||
			(user != null && checkAdminAccess(user));
		setUserCanApproveAndDeclineSuggestions(Boolean(canApprove));
	}, [user]);

	const createdLocal = useMemo(
		() =>
			dayjs.utc(suggestion.Created).tz(timezone).format("MMM D, YYYY • HH:mm"),
		[suggestion.Created, timezone],
	);
	const suggestionComments = useMemo<ProcedureUpdateComment[]>(
		() => (Array.isArray(suggestion.Comments) ? suggestion.Comments : []),
		[suggestion.Comments],
	);

	const statusChangeFormatted = useMemo(() => {
		if (!suggestion.StatusChangeOn) return null;
		const d = dayjs.utc(suggestion.StatusChangeOn).tz(timezone);
		return {
			short: d.format("DD/MM/YY HH:mm:ss"),
			full: d.format("DD/MM/YY HH:mm:ss"),
		};
	}, [suggestion.StatusChangeOn, timezone]);

	const {
		tone: statusTone,
		icon: statusIcon,
		label: statusLabel,
	} = statusVisual(suggestion.Status);
	const localizedStatusLabel = GetLanguageValueAsString(
		translation,
		`ProcedureUpdates.Status.${statusLabel.replace(/[^A-Za-z0-9]+/g, "")}`,
		statusLabel,
	);
	const suggestionStatusKey = useMemo(
		() => normalizeSuggestionStatus(suggestion.Status),
		[suggestion.Status],
	);

	const isSelected = selectedCard;

	const onCardClick = () => {
		handleStepIdSelected(suggestion.StepId);
		onOpenSuggestion?.(suggestion);
	};

	const statusToastText = (status: string) => {
		const s = String(status || "").toLowerCase();
		const step =
			suggestion?.StepId != null ? `Step ${suggestion.StepId}: ` : "";
		if (s === "verified") return `${step}Suggestion verified.`;
		if (s === "declined") return `${step}Suggestion declined.`;
		if (s === "unverified") return `${step}Moved to unverified.`;
		if (s === "closed") return `${step}Suggestion closed.`;
		return `${step}Suggestion updated: ${status}.`;
	};

	const changeStatusWithToast = async (status: string, cmt = "") => {
		if (isUpdatingStatus) return;
		const procedureId =
			suggestion.ProcedureId ?? selectedChecklistProcedureId ?? 0;
		setIsUpdatingStatus(true);
		setPendingStatus(status);
		try {
			await Promise.resolve(
				handleUpdateStatusForCard(suggestion.Id, status, {
					procedureId,
					stepId: suggestion.StepId,
					description: suggestion.Description,
					anonymous: suggestion.Anonymous,
					attachment: suggestion.Attachment,
					declinedComment: cmt,
					checklistId: selectedChecklistId ?? null,
				}),
			);
			void SuccessToast(statusToastText(status));
		} catch {
			void ErrorToast("Failed to update suggestion status.");
		} finally {
			setIsUpdatingStatus(false);
			setPendingStatus(null);
		}
	};

	const openCommentDialog = () => {
		if (isUpdatingStatus) return;
		setCommentModalOpen(true);
	};

	const openDeclineDialog = () => {
		if (isUpdatingStatus) return;
		setDeclineModalOpen(true);
	};

	const closeCommentDialog = () => {
		setCommentModalOpen(false);
		setComment("");
		setIsSubmittingComment(false);
	};

	const submitComment = async () => {
		const text = comment.trim();
		if (!text) return;
		if (isSubmittingComment) return;

		setIsSubmittingComment(true);
		setCommentModalOpen(false);
		setComment("");

		try {
			await AddComment({
				suggestionId: suggestion.Id,
				description: text,
				createdById: user?.Id,
				createdByName: user?.Name,
				created: new Date().toISOString(),
			});

			await SuccessToast("Comment added.");
			await Promise.resolve(refreshSuggestions());
		} catch {
			void ErrorToast("Failed to add comment.");
			setComment(text);
			setCommentModalOpen(true);
		} finally {
			setIsSubmittingComment(false);
		}
	};

	const suggestedChoiceStatus = useMemo(
		() => getSuggestedChoiceStatus(page, suggestion.Status),
		[page, suggestion.Status],
	);

	const suggestedChoices = useMemo(() => {
		const choices = suggestedChoicesByStatus[suggestedChoiceStatus];
		if (suggestedChoiceStatus === "unverified") {
			return choices.filter((choice) => choice !== "close");
		}
		return choices;
	}, [suggestedChoiceStatus]);

	const primaryActions = useMemo(() => {
		// Clear, always-visible action row at bottom (instead of hiding everything behind kebab).
		const actions: Array<{
			key: string;
			label: string;
			icon: React.ReactNode;
			onClick: () => void;
			variant: "outlined" | "text";
			color:
				| "inherit"
				| "primary"
				| "success"
				| "warning"
				| "error"
				| "info"
				| "secondary";
			disabled?: boolean;
		}> = [];

		const canModerate = userCanApproveAndDeclineSuggestions;
		const reopenActionLabel =
			suggestedChoiceStatus === "closed"
				? GetLanguageValueAsString(
						translation,
						"ProcedureUpdates.ReopenUnverified",
						"Reopen",
					)
				: GetLanguageValueAsString(
						translation,
						"ProcedureUpdates.SetToNewUnverified",
						"Unverify",
					);

		suggestedChoices.forEach((choice) => {
			switch (choice) {
				case "verify":
					actions.push({
						key: "verify",
						label: GetLanguageValueAsString(
							translation,
							"Common.Verify",
							"Verify",
						),
						icon: <DoneIcon fontSize="small" />,
						onClick: () => {
							void changeStatusWithToast("Verified");
						},
						variant: "outlined",
						color: "success",
						disabled: !canModerate || isUpdatingStatus,
					});
					break;
				case "decline":
					actions.push({
						key: "decline",
						label: GetLanguageValueAsString(
							translation,
							"Common.Decline",
							"Decline",
						),
						icon: <ChatBubbleOutlineIcon fontSize="small" />,
						onClick: openDeclineDialog,
						variant: "outlined",
						color: "warning",
						disabled: !canModerate || isUpdatingStatus,
					});
					break;
				case "close":
					actions.push({
						key: "close",
						label: GetLanguageValueAsString(
							translation,
							"Common.Close",
							"Close",
						),
						icon: <HighlightOffOutlinedIcon fontSize="small" />,
						onClick: () => {
							void changeStatusWithToast(
								"Closed",
								suggestion.DeclinedComment || "",
							);
						},
						variant: "outlined",
						color: "inherit",
						disabled: !canModerate || isUpdatingStatus,
					});
					break;
				case "unverified":
				default:
					actions.push({
						key: "unverified",
						label: reopenActionLabel,
						icon: <HourglassEmptyIcon fontSize="small" />,
						onClick: () => {
							void changeStatusWithToast("Unverified");
						},
						variant: "outlined",
						color: "primary",
						disabled: !canModerate || isUpdatingStatus,
					});
					break;
			}
		});

		actions.push({
			key: "comment",
			label: GetLanguageValueAsString(
				translation,
				"Common.Comment",
				"Comment",
			),
			icon: <CommentIcon fontSize="small" />,
			onClick: openCommentDialog,
			variant: "outlined",
			color: "primary",
			disabled: isUpdatingStatus,
		});

		return actions;
	}, [
		translation,
		suggestedChoices,
		suggestedChoiceStatus,
		suggestion.DeclinedComment,
		userCanApproveAndDeclineSuggestions,
		isUpdatingStatus,
	]);
	const closeAction = useMemo(
		() => primaryActions.find((a) => a.key === "close"),
		[primaryActions],
	);
	const regularActions = useMemo(
		() => primaryActions.filter((a) => a.key !== "close"),
		[primaryActions],
	);

	const deleteReply = async (commentId: number) => {
		if (deletingReplyId) return;
		setDeletingReplyId(commentId);
		try {
			await Promise.resolve(handleDeleteComment(commentId));
		} finally {
			setDeletingReplyId(null);
		}
	};

	const openCommentMenu = (
		e: React.MouseEvent<HTMLElement>,
		commentId: number,
	) => {
		e.stopPropagation();
		setActiveCommentId(commentId);
		setCommentMenuAnchor(e.currentTarget);
	};

	const closeCommentMenu = () => {
		setActiveCommentId(null);
		setCommentMenuAnchor(null);
	};

	return (
		<>
				<SuggestionCard
					onClick={onCardClick}
					tabIndex={0}
					role="group"
					aria-label={`Suggestion for step ${suggestion.StepId}`}
					data-selected={isSelected ? "true" : "false"}
					data-status={suggestionStatusKey}
					onKeyDown={(e) => {
						if (isTypingTarget(e.target)) return;
						const key = e.key.toLowerCase();
						if (key === "enter" || key === " ") {
							e.preventDefault();
							onCardClick();
						}
					}}
				>
					{/* Description hero */}
					<Box
						onClick={(e) => e.stopPropagation()}
						sx={{
							mb: 1.5,
							"& p, & li": { mb: 0.3, lineHeight: 1.65, fontSize: "1rem", fontWeight: 600 },
							"& p:last-child": { mb: 0 },
							wordBreak: "break-word",
						}}
					>
						{suggestion.Description ? (
							<SATOSContentRender
								isNotProcedureRender
								rehypePlugins={[rehypeRaw]}
								content={suggestion.Description}
							/>
						) : (
							<Typography variant="body1" sx={{ fontWeight: 600, color: "text.secondary" }}>—</Typography>
						)}
					</Box>

					{/* Attachment */}
					{!!suggestion.Attachment && (
						<FileChip
							size="small"
							variant="outlined"
							icon={<AttachFileOutlinedIcon fontSize="small" />}
							label={
								<Typography variant="caption" noWrap sx={{ maxWidth: 260 }}>
									{suggestion.Attachment.FileName}
								</Typography>
							}
							onClick={(e) => {
								e.stopPropagation();
								window.open(suggestion.Attachment?.Url, "_blank");
							}}
							sx={{ mb: 1.5 }}
						/>
					)}

					{/* Divider */}
					<Box sx={(t) => ({ borderTop: `1px solid ${alpha(t.palette.divider, 0.7)}`, mb: 1.5 })} />

					{/* Author row */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
						<Avatar
							{...stringAvatar(suggestion.CreatedBy?.Name || "U")}
							sx={{ width: 36, height: 36, flexShrink: 0, fontSize: "0.8rem" }}
						/>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
								{suggestion.CreatedBy?.Name || "Unknown"}
							</Typography>
							<Typography variant="caption" sx={{ color: "text.secondary" }}>
								{createdLocal}
							</Typography>
						</Box>

						<StatusPill
							size="small"
							variant="filled"
							color={toneToMuiColor(statusTone)}
							icon={statusIcon}
							label={localizedStatusLabel}
							sx={{ flexShrink: 0 }}
						/>

						{selectedChecklistProcedureId != null && (
							<Tooltip title={`Step ${suggestion.StepId} — open procedure`} arrow>
								<Box component="span">
									<SATOSLink
										to="AccessProcedure"
										target="_blank"
										params={[selectedChecklistProcedureId]}
										stopPropagation
										aria-label={`Step ${suggestion.StepId} — open procedure`}
									>
										<IconButton size="small" sx={iconBtnSx} onClick={(e) => e.stopPropagation()}>
											<OpenInNewIcon sx={{ fontSize: 16 }} />
										</IconButton>
									</SATOSLink>
								</Box>
							</Tooltip>
						)}
					</Box>

					{/* Status change */}
					{statusChangeFormatted && (
						<Tooltip title={statusChangeFormatted.full} arrow>
							<Stack
								direction="row"
								alignItems="center"
								spacing={0.8}
								sx={{ mt: 1, color: "text.secondary", width: "fit-content" }}
								onClick={(e) => e.stopPropagation()}
							>
								<Dot status={String(suggestion.Status || "")} />
								<Typography variant="caption" sx={{ fontWeight: 700 }}>
									{localizedStatusLabel}
									{suggestion.StatusUserName ? ` by ${suggestion.StatusUserName}` : ""}
								</Typography>
								<Typography variant="caption" sx={{ opacity: 0.8 }}>
									· {statusChangeFormatted.short}
								</Typography>
							</Stack>
						</Tooltip>
					)}

					{/* Replies */}
					{suggestionComments.length > 0 && (
						<Thread
							onClick={(e) => e.stopPropagation()}
							sx={{ mt: 1.5 }}
							data-expanded={expanded ? "true" : "false"}
						>
							<Button
								size="small"
								onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
								startIcon={expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
								sx={threadToggleBtnSx}
							>
								{expanded
									? <Translate String="ProcedureUpdates.HideReplies">Hide replies ({suggestionComments.length})</Translate>
									: <Translate String="ProcedureUpdates.ViewReplies">View replies ({suggestionComments.length})</Translate>
								}
							</Button>

							{expanded && (
								<Box sx={{ mt: 0.5 }}>
									{suggestionComments
										.slice()
										.sort((a, b) => new Date(a.Created).valueOf() - new Date(b.Created).valueOf())
										.map((c) => {
											const replyDate = dayjs.utc(c.Created).tz(timezone).format("MMM D, YYYY HH:mm:ss");
											return (
												<CommentRow key={c.Id}>
													<Avatar
														{...stringAvatar(c.CreatedByName || "U")}
														sx={{ width: 28, height: 28, mt: 0.2, fontSize: "0.75rem" }}
													/>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
															<Typography variant="caption" sx={{ fontWeight: 700 }}>
																{c.CreatedByName || "Unknown"}
															</Typography>
															<Typography variant="caption" sx={{ color: "text.secondary" }}>
																{replyDate}
															</Typography>
														</Box>
														<Box sx={{ mt: 0.35, "& p": { mb: 0.2 } }}>
															<SATOSContentRender isNotProcedureRender rehypePlugins={[rehypeRaw]} content={c.Description} />
														</Box>
													</Box>
													<Box className="commentActions">
														<Tooltip title={<Translate String="Common.More">More</Translate>}>
															<IconButton
																size="small"
																onClick={(e) => openCommentMenu(e, c.Id)}
																sx={iconBtnSx}
																aria-label={GetLanguageValueAsString(translation, "ProcedureUpdates.CommentActions", "Comment actions")}
															>
																<MoreVertIcon sx={{ fontSize: 18 }} />
															</IconButton>
														</Tooltip>
													</Box>
												</CommentRow>
											);
										})}
								</Box>
							)}
						</Thread>
					)}

					{/* Decline reason */}
					{!!suggestion.DeclinedComment && (
						<DeclineCallout onClick={(e) => e.stopPropagation()} sx={{ mt: 1.5 }}>
							<Stack direction="row" spacing={0.75} alignItems="center">
								<ErrorIcon sx={{ fontSize: 16, color: "error.main" }} />
								<Typography variant="caption" sx={{ fontWeight: 800, color: "error.main", letterSpacing: 0.2 }}>
									<Translate String="ProcedureUpdates.DeclinedCommentLabel">Decline reason</Translate>
								</Typography>
							</Stack>
							<Typography variant="body2" sx={{ mt: 0.35 }}>{suggestion.DeclinedComment}</Typography>
						</DeclineCallout>
					)}

					{/* Actions */}
					<ActionRow onClick={(e) => e.stopPropagation()} sx={{ mt: 1.5 }}>
						{regularActions.map((a, i) => (
							<Button
								key={a.key}
								onClick={(e) => { e.stopPropagation(); a.onClick(); }}
								size="small"
								disabled={a.disabled}
								variant={i === 0 && a.key !== "comment" ? "contained" : "outlined"}
								color={a.color}
								startIcon={a.icon}
								sx={actionBtnSx}
							>
								{a.label}
							</Button>
						))}
						{closeAction && (
							<Button
								onClick={(e) => { e.stopPropagation(); closeAction.onClick(); }}
								size="small"
								disabled={closeAction.disabled}
								variant="outlined"
								color={closeAction.color}
								startIcon={closeAction.icon}
								sx={[actionBtnSx, closeBtnSx]}
							>
								{closeAction.label}
							</Button>
						)}
					</ActionRow>

					{!userCanApproveAndDeclineSuggestions && (
						<Typography variant="caption" sx={{ color: "text.secondary", mt: 0.6 }}>
							<Translate String="ProcedureUpdates.ModerationRestricted">
								Moderation actions are restricted.
							</Translate>
						</Typography>
					)}
				</SuggestionCard>

			{/* Comment menu (per comment) */}
			<CompactMenu
				anchorEl={commentMenuAnchor}
				open={Boolean(commentMenuAnchor)}
				onClose={closeCommentMenu}
				onClick={(e) => e.stopPropagation()}
			>
				<MenuItem
					onClick={() => {
						const id = activeCommentId;
						closeCommentMenu();
						if (id == null) return;
						AlertConfirmDeleteComment(() => {
							void deleteReply(id);
						}, id);
					}}
					disabled={Boolean(deletingReplyId)}
					sx={(t) => ({
						"&:hover .MuiListItemIcon-root": { color: t.palette.error.main },
					})}
				>
					<ListItemIcon>
						{deletingReplyId ? (
							<CircularProgress size={14} color="inherit" />
						) : (
							<ClearIcon fontSize="small" />
						)}
					</ListItemIcon>
					<ListItemText
						primary={
							deletingReplyId ? (
								<Translate String="ProcedureUpdates.Deleting">
									Deleting...
								</Translate>
							) : (
								<Translate String="ProcedureUpdates.DeleteReply">
									Delete reply
								</Translate>
							)
						}
						secondary={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1163C17.ThisCannotBeUndone", "This cannot be undone")}
						sx={{ my: 0 }}
					/>
				</MenuItem>
			</CompactMenu>

			{/* Decline dialog */}
			<DocsDialog
				open={isDeclineModalOpen}
				onClose={() => setDeclineModalOpen(false)}
				fullWidth
				maxWidth="xs"
				onClick={(e) => e.stopPropagation()}
			>
				<DialogTitle
					component="div"
					sx={{
						pb: 1,
						display: "flex",
						alignItems: "flex-start",
						gap: 1,
						borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
						bgcolor: "background.default",
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
						<Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.06em", lineHeight: 1.1 }}>
							<Translate String="Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1189C130.ReviewAction">Review action</Translate>
						</Typography>
						<Typography variant="subtitle1" fontWeight={900}>
							<Translate String="MaterialHandling.DeclineSuggestion">
								Decline suggestion
							</Translate>
						</Typography>
					</Box>
					<Box sx={{ flex: 1 }} />
					<IconButton
						aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1200C18.Close", "Close")}
						size="small"
						onClick={() => setDeclineModalOpen(false)}
						disabled={isUpdatingStatus}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ pt: 2 }}>
					<DialogContentText sx={{ mb: 1.5, color: "text.secondary" }}>
						<Translate String="MaterialHandling.DeclineSuggestionParagraph">
							Add a short reason so others understand the decision.
						</Translate>
					</DialogContentText>

					<DocsTextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						value={declineComment}
						onChange={(e) => setDeclineComment(e.target.value)}
						onKeyDown={(e) => {
							if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
								e.preventDefault();
								void changeStatusWithToast("Declined", declineComment);
								setDeclineModalOpen(false);
								setDeclineComment("");
							}
						}}
						placeholder={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1231C19.ReasonForDecline", "Reason for decline…")}
						label={
							<Translate String="MaterialHandling.DeclineComment">
								Decline reason
							</Translate>
						}
					/>
				</DialogContent>

				<DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5, gap: 1 }}>
					<Button
						onClick={() => {
							setDeclineModalOpen(false);
							setDeclineComment("");
						}}
						variant="text"
						color="inherit"
						sx={docsBtnSx}
						startIcon={<CloseIcon fontSize="small" />}
						disabled={isUpdatingStatus}
					>
						<Translate String="MaterialHandling.Cancel">Cancel</Translate>
					</Button>
					<Button
						onClick={() => {
							void changeStatusWithToast("Declined", declineComment);
							setDeclineModalOpen(false);
							setDeclineComment("");
						}}
						disabled={!userCanApproveAndDeclineSuggestions || isUpdatingStatus}
						variant="contained"
						color="error"
						sx={docsPrimaryBtnSx}
						startIcon={
							isUpdatingStatus &&
							pendingStatus?.toLowerCase() === "declined" ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<SaveIcon fontSize="small" />
							)
						}
					>
						<Translate String="MaterialHandling.Submit">
							{isUpdatingStatus && pendingStatus?.toLowerCase() === "declined"
								? "Submitting..."
								: "Submit"}
						</Translate>
					</Button>
				</DialogActions>
			</DocsDialog>

			{/* Comment dialog */}
			<DocsDialog
				open={isCommentModalOpen}
				onClose={closeCommentDialog}
				fullWidth
				maxWidth="xs"
				onClick={(e) => e.stopPropagation()}
			>
				<DialogTitle
					component="div"
					sx={{
						pb: 1,
						display: "flex",
						alignItems: "flex-start",
						gap: 1,
						borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
						bgcolor: "background.default",
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
						<Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.06em", lineHeight: 1.1 }}>
							<Translate String="Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1302C130.Discussion">Discussion</Translate>
						</Typography>
						<Typography variant="subtitle1" fontWeight={900}>
							<Translate String="ProcedureUpdates.AddCommentSuggestion">
								Add comment
							</Translate>
						</Typography>
					</Box>
					<Box sx={{ flex: 1 }} />
					<IconButton
						aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1313C18.Close", "Close")}
						size="small"
						onClick={closeCommentDialog}
						disabled={isSubmittingComment}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ pt: 2 }}>
					<DialogContentText sx={{ mb: 1.5, color: "text.secondary" }}>
						<Translate String="ProcedureUpdates.AddCommentSuggestion.Caption">
							Write a comment for discussion or context.
						</Translate>
					</DialogContentText>

					<DocsTextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						onKeyDown={(e) => {
							if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
								e.preventDefault();
								void submitComment();
							}
						}}
						placeholder={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.CustomCardSuggestion.L1342C19.AddAComment", "Add a comment…")}
						label={
							<Translate String="ProcedureUpdates.AddCommentSuggestion.TextField">
								Your comment
							</Translate>
						}
					/>
				</DialogContent>

				<DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5, gap: 1 }}>
					<Button
						onClick={closeCommentDialog}
						variant="text"
						color="inherit"
						sx={docsBtnSx}
						startIcon={<CloseIcon fontSize="small" />}
						disabled={isSubmittingComment}
					>
						<Translate String="MaterialHandling.Cancel">Cancel</Translate>
					</Button>
					<Button
						onClick={() => {
							void submitComment();
						}}
						disabled={isSubmittingComment || !comment.trim()}
						variant="contained"
						color="primary"
						sx={docsPrimaryBtnSx}
						startIcon={
							isSubmittingComment ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<SaveIcon fontSize="small" />
							)
						}
					>
						{isSubmittingComment
							? GetLanguageValueAsString(
									translation,
									"ProcedureUpdates.Commenting",
									"Commenting...",
								)
							: GetLanguageValueAsString(
									translation,
									"Common.Comment",
									"Comment",
								)}
					</Button>
				</DialogActions>
			</DocsDialog>
		</>
	);
};

export default CustomCard;

/* ------------------------- styles ------------------------- */

const SuggestionCard = styled(Card)(({ theme }) => ({
	position: "relative",
	overflow: "hidden",
	borderRadius: 12,
	border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
	boxShadow: theme.palette.mode === "dark" ? "none" : theme.shadows[1],
	padding: theme.spacing(1.4),
	background: theme.palette.background.paper,
	cursor: "pointer",
	transition:
		"box-shadow .18s ease, border-color .18s ease, transform .18s ease",
	"&[data-selected='true']": {
		boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}, 0 16px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
		borderColor: alpha(theme.palette.primary.main, 0.28),
	},
	"&:hover": {
		borderColor: alpha(theme.palette.text.primary, 0.16),
		boxShadow: theme.palette.mode === "dark" ? theme.shadows[4] : theme.shadows[4],
		transform: "translateY(-1px)",
	},
	"&:focus-visible": {
		outline: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
		outlineOffset: 3,
	},
	"&::before": {
		content: '""',
		position: "absolute",
		left: 0,
		top: 0,
		bottom: 0,
		width: 3,
		background: alpha(theme.palette.warning.main, 0.72),
	},
	"&[data-status='verified']::before": {
		background: alpha(theme.palette.success.main, 0.85),
	},
	"&[data-status='declined']::before": {
		background: alpha(theme.palette.error.main, 0.85),
	},
	"&[data-status='closed']::before": {
		background: alpha(theme.palette.text.disabled, 0.95),
	},
}));

const ContentSurface = styled(Box)(({ theme }) => ({
	borderRadius: 10,
	border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
	background: alpha(
		theme.palette.background.paper,
		theme.palette.mode === "dark" ? 0.64 : 0.88,
	),
	padding: theme.spacing(1.25, 1.3),
	fontSize: "0.98rem",
	boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.4)}`,
}));

const StatusPill = styled(Chip)(({ theme }) => ({
	height: 24,
	borderRadius: 2,
	fontWeight: 800,
	letterSpacing: 0.1,
	"& .MuiChip-label": {
		paddingLeft: theme.spacing(0.8),
		paddingRight: theme.spacing(1),
	},
	"& .MuiChip-icon": { marginLeft: theme.spacing(0.7) },
}));

const FileChip = styled(Chip)(({ theme }) => ({
	borderRadius: 2,
	borderColor: alpha(theme.palette.divider, 0.9),
	background: theme.palette.background.paper,
	"&:hover": {
		background: theme.palette.action.hover,
		borderColor: alpha(theme.palette.primary.main, 0.6),
	},
}));

const Thread = styled(Box)(({ theme }) => ({
	padding: theme.spacing(0.85, 0.9, 0.55, 1.05),
	borderRadius: 10,
	background: alpha(
		theme.palette.background.paper,
		theme.palette.mode === "dark" ? 0.6 : 0.88,
	),
	border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
}));

const CommentRow = styled(Box)(({ theme }) => ({
	display: "flex",
	gap: theme.spacing(1),
	alignItems: "flex-start",
	padding: theme.spacing(0.9, 0.2),
	borderBottom: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
	"&:last-of-type": { borderBottom: 0 },

	// hide actions until hover (reduces noise)
	"& .commentActions": {
		opacity: 0,
		transition: "opacity .12s ease",
	},
	"&:hover .commentActions": {
		opacity: 1,
	},
}));

const DeclineCallout = styled(Box)(({ theme }) => ({
	borderRadius: 10,
	border: `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
	background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.03)} 100%)`,
	padding: theme.spacing(1.35, 1.5),
}));

const ActionRow = styled(Box)(({ theme }) => ({
	display: "flex",
	gap: theme.spacing(1),
	alignItems: "center",
	flexWrap: "nowrap",
	overflowX: "auto",
	paddingTop: theme.spacing(0.75),
	marginTop: theme.spacing(0.2),
	border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
	background: alpha(
		theme.palette.background.paper,
		theme.palette.mode === "dark" ? 0.56 : 0.88,
	),
	borderRadius: 10,
	paddingInline: theme.spacing(0.9),
	paddingBottom: theme.spacing(0.4),
	"& .MuiButton-startIcon": {
		marginRight: theme.spacing(0.5),
	},
}));

const Dot: React.FC<{ status: string }> = ({ status }) => (
	<Box
		sx={(t) => {
			const s = status.toLowerCase();
			const c =
				s === "verified"
					? t.palette.success.main
					: s === "declined"
						? t.palette.error.main
						: s === "closed"
							? t.palette.text.disabled
							: t.palette.warning.main;
			return {
				width: 7,
				height: 7,
				borderRadius: 2,
				bgcolor: c,
				boxShadow: `0 0 0 2px ${alpha(c, 0.18)}`,
			};
		}}
	/>
);


const DocsDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiPaper-root": {
		borderRadius: 2,
		border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
		boxShadow: theme.shadows[6],
	},
}));

const DocsTextField = styled(TextField)(({ theme }) => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: 2,
		background: theme.palette.background.paper,
	},
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: alpha(theme.palette.divider, 0.9),
	},
	"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: alpha(theme.palette.text.primary, 0.22),
	},
	"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: theme.palette.primary.main,
		borderWidth: 2,
	},
}));

const iconBtnSx = {
	width: 32,
	height: 32,
	borderRadius: 2.5,
	border: (t: Theme) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
	bgcolor: "background.paper",
	color: "text.secondary",
	"&:hover": { bgcolor: "action.hover", color: "text.primary" },
};

const threadToggleBtnSx = {
	textTransform: "none",
	px: 0.75,
	minWidth: 0,
	borderRadius: 2.5,
	fontWeight: 750,
	"&:hover": {
		bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.06),
	},
};

const actionBtnSx = {
	borderRadius: 2.5,
	textTransform: "none",
	fontWeight: 800,
	px: 1.25,
	py: 0.45,
	boxShadow: "none",
	minHeight: 34,
	transition: "all .15s ease",
	"&:hover": {
		boxShadow: "none",
		transform: "translateY(-1px)",
	},
};

const closeBtnSx = {
	borderColor: (t: Theme) => alpha(t.palette.text.primary, 0.22),
	color: "text.secondary",
	backgroundColor: "background.paper",
};

const docsBtnSx = {
	borderRadius: 2.5,
	textTransform: "none",
	fontWeight: 750,
	px: 1.25,
	minHeight: 38,
};

const docsPrimaryBtnSx = {
	borderRadius: 2.5,
	textTransform: "none",
	fontWeight: 900,
	px: 1.75,
	minHeight: 38,
	boxShadow: "none",
};
